import React from 'react';
import { TechLayout } from '../../components/tech/TechLayout';
import { H3, Lead, TCap, Fig, Num } from '../../components/tech/TechUI';

const TONES = { good: ['rgba(34,197,94,0.12)', '#22C55E'], warn: ['rgba(255,208,0,0.12)', '#FFD000'], bad: ['rgba(239,68,68,0.12)', '#EF4444'] };
const box = (x, y, w, h, t1, t2, t3, tone) => {
  const [fill, stroke] = TONES[tone] || ['rgba(255,255,255,0.04)', 'rgba(255,122,0,0.6)'];
  const cx = x + w / 2, sub = 'fill="rgba(255,255,255,0.5)" font-size="11" text-anchor="middle"';
  return `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="8" fill="${fill}" stroke="${stroke}" stroke-width="1.2"/>` +
    `<text x="${cx}" y="${y + (t3 ? 19 : 22)}" text-anchor="middle" fill="#fff" font-size="13" font-weight="600">${t1}</text>` +
    `<text x="${cx}" y="${y + (t3 ? 36 : 39)}" ${sub}>${t2}</text>` + (t3 ? `<text x="${cx}" y="${y + 51}" ${sub}>${t3}</text>` : '');
};

const FLOW_SVG = `<svg viewBox="0 0 900 215" style="width:100%;min-width:720px;display:block" role="img" aria-label="Recording pipeline from upload to delivery" font-family="Inter, sans-serif">
<g stroke="rgba(255,255,255,0.35)" stroke-width="1.4" fill="none"><path d="M140 69 H180"/><path d="M330 60 L380 35"/><path d="M330 69 L380 105"/><path d="M330 80 L380 175"/><path d="M510 35 H530"/><path d="M710 35 H740"/><path d="M510 105 C560 105 560 66 560 66" stroke-dasharray="4 3"/></g>
${box(10, 40, 130, 58, 'Upload', 'straight to storage')}${box(180, 40, 150, 58, 'Automatic checks', 'on the stored files')}
${box(380, 10, 130, 50, 'Passed', 'to the pipeline', null, 'good')}${box(380, 80, 130, 50, 'Human review', 'a person decides', null, 'warn')}
${box(380, 150, 130, 50, 'Blocked', 'never served', null, 'bad')}${box(530, 4, 180, 62, 'Annotation pipeline', 'category, task, object', 'and metadata stored')}
${box(740, 10, 150, 50, 'Delivered', 'delivery API', null, 'good')}
<text x="575" y="92" fill="rgba(255,255,255,0.5)" font-size="11">promoted with a recorded reason</text></svg>`;

const Flow = () => (
  <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[#111111] p-4" data-testid="qc-flow" dangerouslySetInnerHTML={{ __html: FLOW_SVG }} />
);

const GROUPS = [
  ['Passed: every segment meets all of these', 'text-emerald-400', [
    ['Complete segment', <>Every segment has all <Num>6</Num> camera videos and all <Num>3</Num> IMU logs</>],
    ['One clock', "All cameras and IMUs of a segment are timestamped by the cap's single clock"],
    ['Camera sync', <>The <Num>6</Num> cameras of a segment start within <Num>34 ms</Num>, one frame at <Num>30 fps</Num></>],
    ['IMU sync', <>Each IMU samples at <Num>≥ 190 Hz</Num> with no gaps and covers the whole segment; head motion from the gyroscope matches the motion seen by the cameras (correlation <Num>≥ 0.90</Num>, offset <Num>≤ 1</Num> frame)</>],
    ['Same recording time', <>The camera videos of a segment differ in length by <Num>≤ 2 s</Num></>],
    ['Decoding', <>Every video in the session opens and decodes, with <Num>0</Num> errors</>],
    ['Duplicate session', 'Fingerprint matches no session already in the dataset'],
    ['Duplicate file', 'No video identical to one in another session'],
  ]],
  ['Human review', 'text-[#FFD000]', [
    ['Short segment', <>Under <Num>300 s</Num></>],
    ['Blur', <>Sharpness (Laplacian variance) under <Num>150</Num></>],
    ['Low light', <>Over <Num>5%</Num> of frames dark (<Num>&lt; 40</Num> of 255) or blown out (<Num>&gt; 215</Num>)</>],
  ]],
  ['Blocked', 'text-red-400', [['Any passed check fails', 'The session is refused: kept, never delivered']]],
];

const QcTable = () => (
  <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[#111111]" data-testid="table-qc">
    <table className="w-full text-sm border-collapse min-w-[560px]">
      <thead><tr>{['Check', 'Threshold'].map((h) => <th key={h} className="px-4 py-3 text-left font-mono text-[11px] tracking-[0.14em] uppercase text-white/40 font-semibold border-b border-white/10">{h}</th>)}</tr></thead>
      <tbody>
        {GROUPS.map(([g, color, rows]) => (
          <React.Fragment key={g}>
            <tr className="bg-white/[0.03]"><th colSpan={2} className={`px-4 py-2.5 text-left text-xs font-semibold tracking-wide uppercase ${color}`}>{g}</th></tr>
            {rows.map(([k, v]) => (
              <tr key={k} className="border-t border-white/[0.06] hover:bg-white/[0.02] transition-colors">
                <td className="px-4 py-3 align-top text-white/90 font-medium w-[30%]">{k}</td>
                <td className="px-4 py-3 align-top text-white/60 leading-relaxed">{v}</td>
              </tr>
            ))}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  </div>
);

export default function QualityPage() {
  return (
    <TechLayout slug="quality" lead={<Lead>Every upload is checked automatically. Only recordings that pass, are labelled and are filed ever reach a lab.</Lead>}>
      <H3 id="how-a-recording-is-checked">How a recording is checked</H3>
      <Fig cap={<>The life of a recording. <b className="text-white/75">Human review</b>: a check failed, but a person reviews the recording and can promote it with a reason. <b className="text-white/75">Annotation pipeline</b>: stores the category, task, objects and metadata for every recording that passes. <b className="text-white/75">Blocked</b>: refused outright, kept but never delivered. Every step is logged.</>}>
        <Flow />
      </Fig>

      <H3 id="the-checks">The checks</H3>
      <TCap title="Automatic checks.">The checks and the thresholds we apply. Each session is checked segment by segment and is passed, sent to human review or blocked. Segments sampled for the benchmark (see Benchmarks) must also have <Num>0</Num> dropped frames (a gap over <Num>1.5</Num> frame intervals counts as a drop).</TCap>
      <QcTable />

      <H3 id="delivery">Delivery</H3>
      <Lead>Labs sign in to our delivery API and follow a feed of filed recordings, downloading each one straight from storage. Nothing unfiled is ever visible to them.</Lead>
    </TechLayout>
  );
}
